import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../../environments/environment';

export interface Program {
  id: string;
  program_id?: string;
  program_name: string;
  event_type: string;
  location: string;
  program_manager: string;
  date: string;
  patients_enrolled: number;
  organization_id: string;
  created_at: string;
  updated_at: string;
}

export interface CreateProgramRequest {
  program_id?: string;
  program_name: string;
  event_type: string;
  location: string;
  program_manager: string;
  date: string;
}

export interface UpdateProgramRequest {
  program_id?: string;
  program_name?: string;
  event_type?: string;
  location?: string;
  program_manager?: string;
  date?: string;
}

export interface ProgramsResponse {
  success: boolean;
  data: {
    programs: Program[];
    totalPages: number;
    currentPage: number;
    totalPrograms: number;
  };
}

@Injectable({
  providedIn: 'root',
})
export class ProgramsApiService {
  private readonly apiUrl = environment.apiUrl;

  constructor(private http: HttpClient) {}

  /**
   * Get programs with pagination and optional search
   * GET /api/v1/programs/
   */
  getPrograms(page: number = 1, limit: number = 10, search?: string): Observable<any> {
    let url = `${this.apiUrl}/programs/?page=${page}&limit=${limit}`;
    if (search) {
      url += `&search=${encodeURIComponent(search)}`;
    }
    return this.http.get<any>(url);
  }

  /**
   * Get programs for a sub-organization (super admin only)
   * GET /api/v1/programs/organization/{organization_id}/programs
   */
  getProgramsBySubOrganization(organizationId: string, page: number = 1, limit: number = 10, search?: string): Observable<any> {
    let url = `${this.apiUrl}/programs/organization/${organizationId}/programs?page=${page}&limit=${limit}`;
    if (search) {
      url += `&search=${encodeURIComponent(search)}`;
    }
    return this.http.get<any>(url);
  }

  /**
   * Get a specific program by ID
   * GET /api/v1/programs/{program_id}
   */
  getProgram(id: string): Observable<Program> {
    return this.http.get<Program>(`${this.apiUrl}/programs/${id}`);
  }

  /**
   * Create a new program in the current organization
   * POST /api/v1/programs/
   */
  createProgram(programData: CreateProgramRequest): Observable<Program> {
    return this.http.post<Program>(`${this.apiUrl}/programs/`, programData);
  }

  /**
   * Create a program in a sub-organization (super admin only)
   * POST /api/v1/programs/organization/{organization_id}/programs
   */
  createProgramSubOrg(organizationId: string, programData: CreateProgramRequest): Observable<Program> {
    return this.http.post<Program>(`${this.apiUrl}/programs/organization/${organizationId}/programs`, programData);
  }

  /**
   * Update an existing program
   * PUT /api/v1/programs/{program_id}
   */
  updateProgram(programId: string, programData: UpdateProgramRequest): Observable<Program> {
    return this.http.put<Program>(`${this.apiUrl}/programs/${programId}`, programData);
  }

  /**
   * Update a program in a sub-organization (super admin only)
   * PUT /api/v1/programs/organization/{organization_id}/programs/{program_id}
   */
  updateProgramSubOrg(organizationId: string, programId: string, programData: UpdateProgramRequest): Observable<Program> {
    return this.http.put<Program>(`${this.apiUrl}/programs/organization/${organizationId}/programs/${programId}`, programData);
  }

  /**
   * Delete a program
   * DELETE /api/v1/programs/{program_id}
   */
  deleteProgram(programId: string): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/programs/${programId}`);
  }

  /**
   * Delete a program in a sub-organization (super admin only)
   * DELETE /api/v1/programs/organization/{organization_id}/programs/{program_id}
   */
  deleteProgramSubOrg(organizationId: string, programId: string): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/programs/organization/${organizationId}/programs/${programId}`);
  }
}
